/**
 * Template de prise de besoin : Politique de sécurité de l'information v1
 * Aligné sur ISO 27001, ITIL 4, COBIT 2019 et NIST CSF 2.0
 * Document maître de référence pour la sécurité de l'information
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePolitiqueSecuriteInformationV1: IntakeTemplate = {
  id: 'politique-securite-information-v1',
  name: 'Prise de besoin Politique de sécurité de l\'information v1',
  description:
    'Questionnaire complet pour élaborer une politique de sécurité de l\'information conforme ISO 27001, ITIL 4, COBIT 2019 et NIST CSF 2.0. Document maître de référence que le plan d\'incident et autres politiques doivent respecter.',
  documentTypeCode: 'INFORMATION_SECURITY_POLICY',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Contexte & Objectifs',
      description: 'Informations sur l\'entreprise et objectifs de la politique',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour la politique de sécurité de l\'information.',
        },
        {
          id: 'secteur_activite',
          label: 'Secteur d\'activité',
          type: 'text',
          placeholder: 'Ex: Manufacturier, Services financiers, Santé, Éducation...',
          helpText: 'Le secteur d\'activité influence les exigences réglementaires et les risques spécifiques.',
        },
        {
          id: 'objectifs_politique',
          label: 'Quels sont les objectifs principaux de cette politique de sécurité de l\'information ?',
          type: 'multiselect',
          options: [
            'Protéger la confidentialité des informations',
            'Assurer l\'intégrité des données',
            'Garantir la disponibilité des systèmes',
            'Respecter les obligations légales et réglementaires',
            'Protéger la réputation de l\'organisation',
            'Réduire les risques de sécurité',
            'Autre',
          ],
          required: true,
          helpText: 'ISO 27001 (A.5.1.1): Définition claire des objectifs pour guider la mise en œuvre de la politique.',
        },
        {
          id: 'portee_politique',
          label: 'Quelle est la portée de cette politique ?',
          type: 'multiselect',
          options: [
            'Toutes les informations de l\'organisation',
            'Informations TI uniquement',
            'Informations sensibles uniquement',
            'Informations clients uniquement',
            'Autre',
          ],
          required: true,
          helpText: 'ISO 27001 (A.5.1.1): Définition claire de la portée pour assurer une application cohérente.',
        },
        {
          id: 'actifs_information',
          label: 'Quels sont les principaux actifs d\'information à protéger ?',
          type: 'multiselect',
          options: [
            'Données clients',
            'Données financières',
            'Données RH',
            'Propriété intellectuelle',
            'Secrets commerciaux',
            'Infrastructure TI',
            'Applications métier',
            'Autre',
          ],
          helpText: 'ISO 27001 (A.8.2.1): Identification des actifs pour établir les mesures de protection appropriées.',
        },
        {
          id: 'ton_document_politique',
          label: 'Quel ton souhaitez-vous pour cette politique ?',
          type: 'select',
          options: [
            'Formel et juridique (ton impératif, sanctions claires)',
            'Pédagogique et collaboratif (ton explicatif, guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la réception et l\'adhésion des parties prenantes.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Gouvernance & Responsabilités',
      description: 'Structure de gouvernance et attribution des responsabilités',
      icon: 'Users',
      questions: [
        {
          id: 'structure_gouvernance',
          label: 'Quelle structure de gouvernance souhaitez-vous pour la sécurité de l\'information ?',
          type: 'select',
          options: [
            'Comité de sécurité de l\'information dédié',
            'Intégration dans le comité TI existant',
            'Responsable sécurité désigné',
            'Gouvernance distribuée par département',
            'À déterminer',
          ],
          helpText: 'COBIT 2019 (APO13): Structure de gouvernance claire essentielle pour la gestion efficace de la sécurité.',
        },
        {
          id: 'responsable_securite',
          label: 'Qui sera responsable de la sécurité de l\'information ?',
          type: 'text',
          placeholder: 'Ex: Directeur TI, Responsable Cybersécurité, DPO, CISO...',
          helpText: 'ISO 27001 (A.5.1.1): Attribution claire du responsable pour imputabilité et coordination.',
        },
        {
          id: 'roles_responsabilites',
          label: 'Quels rôles et responsabilités doivent être définis ?',
          type: 'multiselect',
          options: [
            'Responsable sécurité de l\'information',
            'Gestionnaire de sécurité',
            'Administrateurs systèmes',
            'Utilisateurs',
            'Direction',
            'DPO (Délégué à la protection des données)',
            'Autre',
          ],
          helpText: 'ISO 27001 (A.7.1.2): Définition claire des rôles pour éviter les conflits et assurer la coordination.',
        },
        {
          id: 'imputabilite_securite',
          label: 'Qui a la plus haute autorité et imputabilité en matière de sécurité de l\'information ?',
          type: 'text',
          placeholder: 'Ex: Directeur général, Directeur TI, Président...',
          helpText: 'COBIT 2019 (APO13.01): Attribution de l\'imputabilité ultime pour la sécurité.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Principes & Contrôles',
      description: 'Principes de sécurité et contrôles à mettre en place',
      icon: 'Shield',
      questions: [
        {
          id: 'principes_securite',
          label: 'Quels principes de sécurité souhaitez-vous inclure ?',
          type: 'multiselect',
          options: [
            'Confidentialité (CIA triad)',
            'Intégrité (CIA triad)',
            'Disponibilité (CIA triad)',
            'Authenticité',
            'Non-répudiation',
            'Traçabilité',
            'Autre',
          ],
          helpText: 'ISO 27001 (A.5.1.1): Principes fondamentaux de sécurité de l\'information.',
        },
        {
          id: 'controles_securite',
          label: 'Quels contrôles de sécurité doivent être mis en place ?',
          type: 'multiselect',
          options: [
            'Contrôles d\'accès',
            'Chiffrement des données',
            'Sauvegardes',
            'Monitoring et surveillance',
            'Gestion des vulnérabilités',
            'Gestion des incidents',
            'Formation et sensibilisation',
            'Autre',
          ],
          helpText: 'ISO 27001 (Annexe A): Contrôles essentiels pour protéger les actifs d\'information.',
        },
        {
          id: 'classification_donnees',
          label: 'Souhaitez-vous mettre en place un système de classification des données ?',
          type: 'select',
          options: ['Oui, classification détaillée', 'Oui, classification simple', 'Non', 'À déterminer'],
          helpText: 'ISO 27001 (A.8.2.1): Classification des données essentielle pour appliquer les mesures de protection appropriées.',
        },
        {
          id: 'niveaux_classification',
          label: 'Quels niveaux de classification souhaitez-vous ?',
          type: 'multiselect',
          options: [
            'Public',
            'Interne',
            'Confidentiel',
            'Restreint / Secret',
            'Autre',
          ],
          helpText: 'ISO 27001 (A.8.2.1): Niveaux de classification pour gérer les données selon leur sensibilité.',
        },
        {
          id: 'obligations_reglementaires',
          label: 'Quelles obligations réglementaires doivent être respectées ?',
          type: 'multiselect',
          options: [
            'Loi 25 (protection des renseignements personnels)',
            'RGPD',
            'PCI-DSS',
            'HIPAA',
            'Normes sectorielles spécifiques',
            'Aucune obligation spécifique',
            'Autre',
          ],
          helpText: 'ISO 27001 (A.18.1): Conformité réglementaire essentielle pour éviter les sanctions.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Mise en Œuvre & Conformité',
      description: 'Processus de mise en œuvre et conformité',
      icon: 'CheckCircle',
      questions: [
        {
          id: 'processus_conformite',
          label: 'Quel processus de conformité souhaitez-vous mettre en place ?',
          type: 'select',
          options: [
            'Audits réguliers internes',
            'Audits externes',
            'Auto-évaluations',
            'Revues de conformité',
            'À déterminer',
          ],
          helpText: 'ISO 27001 (A.18.2): Processus de conformité essentiel pour maintenir la sécurité.',
        },
        {
          id: 'formation_securite',
          label: 'Souhaitez-vous rendre la formation en sécurité obligatoire ?',
          type: 'select',
          options: ['Oui, pour tous', 'Oui, pour certains rôles uniquement', 'Non, formation recommandée', 'À déterminer'],
          helpText: 'ISO 27001 (A.7.2.2): Formation essentielle pour assurer la compétence des utilisateurs.',
        },
        {
          id: 'gestion_violations',
          label: 'Comment souhaitez-vous gérer les violations de cette politique ?',
          type: 'select',
          options: [
            'Sanctions disciplinaires progressives',
            'Approche éducative avec correction',
            'Mixte (éducatif pour erreurs, disciplinaire pour violations intentionnelles)',
            'À déterminer',
          ],
          helpText: 'ISO 27001 (A.7.2.3): Procédures claires pour gérer les violations et assurer la conformité.',
        },
        {
          id: 'revision_politique',
          label: 'À quelle fréquence souhaitez-vous réviser cette politique ?',
          type: 'select',
          options: ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Lors de changements majeurs', 'À déterminer'],
          helpText: 'ISO 27001 (A.5.1.1): Révision régulière essentielle pour maintenir la pertinence de la politique.',
        },
      ],
    },
  ],
};





